import { Component, Inject, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { UsersService } from '@shared/services/entities/users.service';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { User, CreateUserPayload, UpdateUserPayload } from '@shared/models/entities/users.model';
import { RoleModel } from '@shared/models/entities/roles.model';
import { normalizePhone, isPhoneUsername } from '@shared/security/phone-alias';

export interface UserFormDialogData {
  /** Usuario a editar; ausente = crear. */
  user?: User;
  /** Roles disponibles (los carga la pantalla padre). */
  roles: RoleModel[];
}

/**
 * Diálogo de alta/edición de usuario, sobre el cascarón compartido
 * (`FormDialogShellComponent`): título fijo, acciones fijas, contenido
 * scrolleable. Guarda él mismo y cierra con `true` si hubo cambios.
 */
@Component({
  selector: 'app-user-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormDialogShellComponent,
  ],
  templateUrl: './user-form-dialog.component.html',
  styleUrl: './user-form-dialog.component.scss',
})
export class UserFormDialogComponent {
  private readonly fb                   = inject(FormBuilder);
  private readonly usersService         = inject(UsersService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialog               = inject(MatDialog);
  private readonly dialogRef            = inject(MatDialogRef<UserFormDialogComponent>);

  readonly isEditing: boolean;
  readonly isCreating: boolean;
  availableRoles: RoleModel[] = [];

  saving = signal(false);
  firmaPreviewUrl = signal<string | null>(null);

  form: FormGroup = this.fb.group(
    {
      name:  ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.email]],
      phone: ['', [UserFormDialogComponent.phoneFormatValidator]],
      role:  [''],
      firma: [''],
    },
    { validators: UserFormDialogComponent.emailOrPhoneRequired }
  );

  constructor(@Inject(MAT_DIALOG_DATA) public data: UserFormDialogData) {
    this.isEditing = !!data?.user;
    this.isCreating = !this.isEditing;
    this.availableRoles = data?.roles ?? [];

    const user = data?.user;
    if (user) {
      this.form.patchValue({
        name:  user.name,
        // Al técnico no se le muestra su alias interno (@tel...), solo su teléfono.
        email: this.isPhoneUser(user) ? '' : user.email,
        phone: user.phone ?? '',
        role:  user.role ?? '',
        firma: user.firma ?? '',
      });
      if (user.firma) {
        this.usersService.resolveImageUrl(user.firma).then(u => this.firmaPreviewUrl.set(u));
      }
      // El identificador de acceso no se puede modificar: email para
      // administrativos, teléfono para técnicos.
      this.form.get('email')?.disable();
      if (this.isPhoneUser(user)) this.form.get('phone')?.disable();
    }
  }

  /** Vacío es válido (el requisito conjunto lo pone emailOrPhoneRequired). */
  private static phoneFormatValidator(control: { value: unknown }) {
    const value = String(control.value ?? '').trim();
    if (!value) return null;
    return normalizePhone(value) ? null : { phoneFormat: true };
  }

  /** Al crear, se exige correo o teléfono (al menos uno). */
  private static emailOrPhoneRequired(group: FormGroup) {
    const email = String(group.get('email')?.value ?? '').trim();
    const phone = String(group.get('phone')?.value ?? '').trim();
    return email || phone ? null : { emailOrPhoneRequired: true };
  }

  private isPhoneUser(user: User): boolean {
    return !user.email || isPhoneUsername(user.email);
  }

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  /** Abre el gestor de archivos para cargar / ver / reemplazar la firma autógrafa. */
  async openFirmaManager(): Promise<void> {
    const id = this.data.user?.id;
    if (!id) {
      await this.customMessageService.open({
        title: 'Aviso', type: 'warning',
        message: 'Guarda primero el usuario para poder subir su firma.', dismissBtn: true,
      });
      return;
    }
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:       this.form.get('firma')?.value ?? '',
        label:       'Firma autógrafa',
        accept:      'image/png,image/jpeg',
        uploadPath:  `users/${id}`,
        uploadFolio: 'firma',
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe((result: string | null) => {
      if (result === null || result === undefined) return; // cancelado
      this.form.get('firma')?.setValue(result);
      this.form.get('firma')?.markAsDirty();
      if (result) {
        this.usersService.resolveImageUrl(result).then(u => this.firmaPreviewUrl.set(u));
      } else {
        this.firmaPreviewUrl.set(null);
      }
    });
  }

  removeFirma(): void {
    this.form.get('firma')?.setValue('');
    this.firmaPreviewUrl.set(null);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }

    // getRawValue() incluye campos deshabilitados (email/phone en modo edit)
    const { name, email, phone, role, firma } = this.form.getRawValue();

    this.saving.set(true);
    try {
      if (this.isCreating) {
        const payload: CreateUserPayload = {
          name,
          email: (email ?? '').trim() || null,
          phone: (phone ?? '').trim() || null,
          role: role || null,
        };
        const created = await this.usersService.createUser(payload);
        const invitacion = payload.email
          ? 'Se envió email de invitación.'
          : 'Se envió la invitación por SMS a su teléfono.';
        await this.customMessageService.open({
          title: '¡Creado!', type: 'success',
          message: `Usuario <b>${created?.name ?? name}</b> creado correctamente. ${invitacion}`,
          duration: 3000,
        });
      } else {
        const payload: UpdateUserPayload = {
          id: this.data.user!.id,
          name,
          phone: (phone ?? '').trim() || null,
          role: role || null,
          firma: firma || null,
        };
        const updated = await this.usersService.updateUser(payload);
        await this.customMessageService.open({
          title: '¡Actualizado!', type: 'success',
          message: `Usuario <b>${updated?.name ?? name}</b> actualizado correctamente.`,
          duration: 2500,
        });
      }
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ocurrió un error al guardar el usuario.';
      await this.customMessageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    } finally {
      this.saving.set(false);
    }
  }
}
